import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, KeyRound, Plug, Loader2 } from "lucide-react";
import { ZapierTestCard } from "@/components/ZapierTestCard";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
const EVENT_BADGE = { imported: "bg-emerald-100 text-emerald-800", listed: "bg-emerald-100 text-emerald-800", duplicate: "bg-slate-100 text-slate-700", suppressed: "bg-amber-100 text-amber-800", rejected: "bg-red-100 text-red-700", rejected_exclusive: "bg-red-100 text-red-700" };

export const CopyField = ({ label, value, testId }) => (
  <div className="mt-3">
    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="flex gap-2">
      <code className="flex-1 break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs" data-testid={testId}>{value}</code>
      <Button variant="outline" size="sm" onClick={() => copy(value)} data-testid={`${testId}-copy`}><Copy className="h-4 w-4" /></Button>
    </div>
  </div>
);

const Steps = ({ url }) => (
  <Card className="mb-5 p-6" data-testid="zapier-steps">
    <h3 className="font-semibold text-slate-900">Connect in Zapier</h3>
    <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-600">
      <li>In Zapier, create a Zap. Trigger: <b>Webhooks by Zapier → Catch Hook</b>. Copy the Zapier hook URL.</li>
      <li>In your MortgageLeads.com account, set lead delivery to <b>Webhook / Post URL</b> and paste the Zapier hook URL. Send a test lead.</li>
      <li>Add an action: <b>Webhooks by Zapier → POST</b>. URL: <code className="break-all text-xs">{url}</code>. Payload type: <b>JSON</b>.</li>
      <li>Under <b>Headers</b> add <code>X-Elite-Key</code> with your integration key.</li>
      <li>Under <b>Data</b>, map the MortgageLeads.com fields: <code>lead_id</code> (required), <code>first_name</code>, <code>last_name</code>, <code>phone</code>, <code>email</code>, <code>address</code>, <code>city</code>, <code>state</code>, <code>zip</code>, <code>property_value</code>, <code>loan_amount</code>, <code>mortgage_balance</code>, <code>ltv</code>, <code>loan_type</code>, <code>loan_purpose</code>, <code>purchase_date</code>, <code>account_id</code>, <code>lead_source</code>, <code>tcpa_consent</code>, <code>consent_text</code>, <code>consent_timestamp</code>, <code>trustedform_url</code>, <code>contact_methods</code>. Leave out anything MortgageLeads.com doesn't send.</li>
      <li>Test the action and turn the Zap on. New leads are listed on the Elite Leads Marketplace for purchase.</li>
    </ol>
    <p className="mt-3 text-xs text-slate-500">AI calls (including AI Autopilot Mode) only happen when the lead's consent allows phone or automated calls, e.g. contact_methods includes "phone" or tcpa_consent is "yes".</p>
  </Card>
);

export default function Integrations() {
  const [info, setInfo] = useState(null);
  const [events, setEvents] = useState([]);
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [a, e] = await Promise.all([api.get("/integrations/astoria"), api.get("/integrations/astoria/events")]);
    setInfo(a.data); setEvents(e.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);
  const generate = async () => {
    if (info?.connected && !window.confirm("Generate a new key? Your current Zap stops working until you paste the new key.")) return;
    setBusy(true);
    try { const { data } = await api.post("/integrations/astoria/key"); setNewKey(data.key); await load(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const toggle = async (v) => {
    try { await api.post("/integrations/astoria/toggle", { enabled: v }); await load(); toast.success(v ? "Connection on" : "Connection off"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!info) return <div className="p-10 text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <PageHeader title="Integrations" subtitle="Automatically import the leads you purchase on MortgageLeads.com (Astoria)." />
      <Card className="mb-5 p-6" data-testid="astoria-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Plug className="h-5 w-5 text-indigo-600" /><h3 className="font-semibold text-slate-900">MortgageLeads.com (Astoria) via Zapier</h3>
            <Badge className={`border-0 ${info.connected && info.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid="astoria-status">{!info.connected ? "Not connected" : info.enabled ? "Connected" : "Off"}</Badge>
          </div>
          {info.connected && <div className="flex items-center gap-2 text-sm text-slate-600">Receiving leads <Switch checked={info.enabled} onCheckedChange={toggle} data-testid="astoria-enabled-switch" /></div>}
        </div>
        <CopyField label="Webhook URL" value={info.webhook_url} testId="astoria-webhook-url" />
        {newKey ? (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="astoria-new-key-box">
            <p className="text-xs font-semibold text-amber-900">Copy your key now. For security it won't be shown again.</p>
            <CopyField label="Integration key (X-Elite-Key header)" value={newKey} testId="astoria-new-key" />
          </div>
        ) : info.connected && <p className="mt-3 text-sm text-slate-600">Integration key ending in <b>…{info.key_last4}</b></p>}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={generate} disabled={busy} data-testid="astoria-generate-key-btn" className="bg-slate-900 hover:bg-slate-800">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1.5 h-4 w-4" />}{info.connected ? "Regenerate key" : "Generate integration key"}
          </Button>
          <span className="text-xs text-slate-500" data-testid="astoria-received">{info.received_count || 0} deliveries received{info.last_received_at ? ` · last ${new Date(info.last_received_at).toLocaleString()}` : ""}</span>
        </div>
      </Card>
      <Steps url={info.webhook_url} />
      <ZapierTestCard />
      <Card className="p-6" data-testid="astoria-events">
        <h3 className="mb-3 font-semibold text-slate-900">Recent deliveries</h3>
        {events.length === 0 ? <p className="text-sm text-slate-400">No leads received yet.</p> : (
          <div className="space-y-2">{events.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm">
              <span>Lead <b>{e.astoria_lead_id || "—"}</b>{e.detail ? ` · ${e.detail}` : ""}</span>
              <span className="flex items-center gap-2"><Badge className={`border-0 ${EVENT_BADGE[e.status] || ""}`}>{e.status.replace("_", " ")}</Badge><span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</span></span>
            </div>))}</div>
        )}
      </Card>
    </div>
  );
}
